/**
 * The kind of a quote in a Job (JOBS-UX-SPEC D2, D3, 1.5; lucid 007).
 *
 * Four kinds, derived and never stored on projects:
 *   - SPLIT: the server reports a split area (the quote was split off).
 *   - IMPORTED: imported, and still at the Rev it was imported at.
 *   - REVISED: its Rev is above its starting Rev (the imported Rev, or 0).
 *   - ORIGINAL: otherwise.
 *
 * A split quote that is later revised stays SPLIT: its Quote # carries the Rev
 * and its card carries the reason, so nothing is lost.
 *
 * The tag is ONE uppercase word and never holds a digit. The Rev stays in the
 * Quote # ("RBB260905 Rev 2"), printed one way on every surface (D3).
 *
 * Pure. The Job bar, Job history, the Run Sheet and Reference Files use it.
 */

export type QuoteKind = 'original' | 'imported' | 'revised' | 'split';

export interface KindInput {
    quoteRev?: number | string | null;
    splitArea?: string | null;
    importedRev?: number | string | null;
}

function whole(value: unknown): number | null {
    if (value === null || value === undefined || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? Math.trunc(n) : null;
}

export function quoteKind(q: KindInput): QuoteKind {
    if (typeof q?.splitArea === 'string' && q.splitArea.trim()) return 'split';
    const rev = whole(q?.quoteRev) ?? 0;
    const imported = whole(q?.importedRev);
    if (imported !== null) return rev > imported ? 'revised' : 'imported';
    return rev > 0 ? 'revised' : 'original';
}

const TAG_WORDS: Record<QuoteKind, 'ORIGINAL' | 'IMPORTED' | 'REVISED' | 'SPLIT'> = {
    original: 'ORIGINAL',
    imported: 'IMPORTED',
    revised: 'REVISED',
    split: 'SPLIT',
};

export function kindTagWord(kind: QuoteKind): 'ORIGINAL' | 'IMPORTED' | 'REVISED' | 'SPLIT' {
    return TAG_WORDS[kind] ?? 'ORIGINAL';
}

/** The words behind a tag, for its title attribute. */
export function kindTagTitle(kind: QuoteKind, area?: string | null): string {
    if (kind === 'split') {
        const name = typeof area === 'string' ? area.trim() : '';
        return name ? `Split quote: ${name}` : 'Split quote';
    }
    if (kind === 'imported') return 'Imported from a quote document';
    if (kind === 'revised') return 'Revised quote. Job history says why.';
    return 'The first quote of this Job';
}

/**
 * REVISED and SPLIT always carry their tag. ORIGINAL and IMPORTED only in a
 * Job of two or more: a tag on every plain quote says nothing.
 */
export function showsKindTag(kind: QuoteKind, jobQuoteCount: number): boolean {
    if (kind === 'revised' || kind === 'split') return true;
    return Number(jobQuoteCount) >= 2;
}
